import { useEffect, useRef, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { ServiceCard } from "@/pages/save/service/ServiceCard";
import type { ServiceCardSpec } from "@/pages/save/service/service-actions";

export interface ServiceMenuGroup {
  titleId: string;
  cards: readonly ServiceCardSpec[];
  accent?: boolean;
}

/**
 * The "Service" button and its popover of action cards, as the profile header has it (ServiceMenu) and
 * the organizational record's documents reuse it: opens on hover or click, closes when the pointer
 * leaves, on a click outside, on Escape or once a card is chosen. What a card does is the caller's
 * (`onAction`), and so are its dialogs, which belong outside this hover zone.
 */
export function ServiceMenuPopover({
  groups,
  onAction,
  busyActions,
  disabledActions,
  note,
  defaultOpen = false,
  onOpen,
  id = "service-menu-open",
  cardIdPrefix = "service-card",
}: {
  groups: readonly ServiceMenuGroup[];
  onAction: (spec: ServiceCardSpec) => void;
  busyActions?: ReadonlySet<string>;
  disabledActions?: ReadonlySet<string>;
  // Under the cards: an error, or why a card cannot be used.
  note?: ReactNode;
  defaultOpen?: boolean;
  // Each time the menu is reached for (hover or click), before it shows.
  onOpen?: () => void;
  id?: string;
  cardIdPrefix?: string;
}) {
  const intl = useIntl();
  const [open, setOpen] = useState(defaultOpen);
  const wrapperRef = useRef<HTMLDivElement>(null);

  // Click/tap outside closes (mouse users also get hover-leave below). No
  // viewport scrim — it would sit above the button and flicker the hover loop.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!wrapperRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const renderCard = (spec: ServiceCardSpec, accent: boolean) => {
    const busy = spec.action ? busyActions?.has(spec.action) === true : false;
    const disabled = busy || (spec.action ? disabledActions?.has(spec.action) === true : false);
    return (
      <ServiceCard
        key={spec.titleId}
        // Die Aktion ist der sprachunabhängige Schlüssel der Karte; die noch
        // nicht gebauten haben keine und fallen auf ihre Nachrichtenkennung zurück.
        id={`${cardIdPrefix}-${spec.action ?? spec.titleId.replace(/\./g, "-")}`}
        icon={spec.icon}
        title={intl.formatMessage({ id: spec.titleId })}
        desc={intl.formatMessage({ id: spec.descId })}
        tags={
          busy
            ? intl.formatMessage({ id: "service.page.exporting", defaultMessage: "Exporting …" })
            : spec.tags
        }
        accent={accent}
        comingSoon={spec.comingSoon}
        disabled={disabled || undefined}
        onClick={
          spec.action
            ? () => {
                setOpen(false);
                onAction(spec);
              }
            : undefined
        }
      />
    );
  };

  return (
    <div
      ref={wrapperRef}
      className="relative inline-block"
      onMouseEnter={() => {
        onOpen?.();
        setOpen(true);
      }}
      onMouseLeave={() => setOpen(false)}
      onKeyDown={(e) => e.key === "Escape" && setOpen(false)}
    >
      <Button
        id={id}
        variant="primary"
        size="compact"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => {
          onOpen?.();
          setOpen((v) => !v);
        }}
      >
        <FormattedMessage id="profile.page.serviceMenu" defaultMessage="Service" />
      </Button>
      {open ? (
        // No top gap so the pointer never crosses dead space (which would
        // flicker the menu). Cards mirror the Documents page Input/Output zone.
        <div className="toggle-pop absolute right-0 top-full z-30 max-h-[80vh] w-[min(92vw,420px)] origin-top-right overflow-auto rounded-md bg-surface p-4 shadow-lg ring-1 ring-ink/10">
          {groups.map((group, index) => (
            <div key={group.titleId} className={index ? "mt-4" : undefined}>
              <div className="smycles-field-label mb-2">
                <FormattedMessage id={group.titleId} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {group.cards.map((spec) => renderCard(spec, group.accent === true))}
              </div>
            </div>
          ))}
          {note ? <div className="smycles-field-meta mt-3">{note}</div> : null}
        </div>
      ) : null}
    </div>
  );
}
